/**
 * Deterministic categorical colours for charts.
 *
 * The first six slots are fixed and overridable. Beyond that, colours are
 * generated in OKLCH so the perceptual constraints hold by construction rather
 * than by inspection: hues are spread by the golden angle, lightness is pinned
 * to a tier that clears 3:1 on white, and chroma is clamped to the sRGB gamut
 * for that hue so no colour ever falls back to gray.
 *
 */

export const BRAND_COLORS = Object.freeze([
  'var(--chart-color-1, #1b8375)', // teal — brand
  'var(--chart-color-2, #c26b3e)', // terracotta
  'var(--chart-color-3, #3f6f9c)', // slate blue
  'var(--chart-color-4, #b04f68)', // rose
  'var(--chart-color-5, #6f5f9e)', // muted violet
  'var(--chart-color-6, #5f8a4c)', // moss green
]);

/** Residual, "Other" and NULL buckets. 3.32:1 on white. */
export const NEUTRAL_COLOR = 'var(--chart-color-neutral, #868e96)';

/** Spreads any number of hues about as evenly as possible without knowing N. */
const GOLDEN_ANGLE = 137.508;

/**
 * Lightness tiers for generated colours, walked after each full hue rotation so
 * crowded hues stay separable by lightness.
 */
const LIGHTNESS_TIERS = [0.6, 0.53, 0.46];

/** Minimum contrast against the white chart surface. */
const MIN_CONTRAST = 3;

/** Matched to the six fixed slots, which sit at 0.09–0.13, so the tail does not
    come out louder than the palette it continues. */
const MAX_CHROMA = 0.115;

const HUES_PER_TIER = 12;

const srgb = v => {
  const c = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
  return Math.round(Math.min(1, Math.max(0, c)) * 255);
};

const inGamut = channels => channels.every(v => v >= -0.0002 && v <= 1.0002);

function oklchToLinearRgb(lightness, chroma, hueDegrees) {
  const hue = (hueDegrees * Math.PI) / 180;
  const a = chroma * Math.cos(hue);
  const b = chroma * Math.sin(hue);

  const l = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;

  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

/** Highest chroma sRGB can hold at this lightness and hue. */
function maxChroma(lightness, hueDegrees) {
  let low = 0;
  let high = 0.4;

  for (let i = 0; i < 24; i += 1) {
    const mid = (low + high) / 2;
    if (inGamut(oklchToLinearRgb(lightness, mid, hueDegrees))) low = mid;
    else high = mid;
  }

  return low;
}

/**
 * @param {number} lightness OKLCH L
 * @param {number} hueDegrees OKLCH hue
 * @returns {string} hex, with chroma clamped to what sRGB can hold
 */
export function oklch(lightness, hueDegrees) {
  const chroma = Math.min(maxChroma(lightness, hueDegrees), MAX_CHROMA);
  const channels = oklchToLinearRgb(lightness, chroma, hueDegrees);

  return `#${channels
    .map(srgb)
    .map(v => v.toString(16).padStart(2, '0'))
    .join('')}`;
}

const relativeLuminance = hex =>
  [1, 3, 5]
    .map(i => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map(c => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4))
    .reduce((sum, c, i) => sum + c * [0.2126, 0.7152, 0.0722][i], 0);

const contrastOnWhite = hex => 1.05 / (relativeLuminance(hex) + 0.05);

/**
 * How light a hue can be and still clear MIN_CONTRAST varies with the hue —
 * a yellow-green runs out of contrast well before a blue does. Rather than pick
 * tiers conservative enough for the worst hue, darken until the colour passes.
 */
function readable(lightness, hueDegrees) {
  let candidate = lightness;

  for (let i = 0; i < 12; i += 1) {
    const hex = oklch(candidate, hueDegrees);
    if (contrastOnWhite(hex) >= MIN_CONTRAST) return hex;
    candidate -= 0.02;
  }

  return oklch(candidate, hueDegrees);
}

/**
 * Colour for the nth categorical slot.
 *
 * @param {number} index zero-based
 * @returns {string} hex
 */
export function colorAt(index) {
  if (index < BRAND_COLORS.length) return BRAND_COLORS[index];

  const generated = index - BRAND_COLORS.length;
  const tier =
    LIGHTNESS_TIERS[
      Math.floor(generated / HUES_PER_TIER) % LIGHTNESS_TIERS.length
    ];

  return readable(tier, (generated * GOLDEN_ANGLE) % 360);
}

/**
 * Colours for a whole series, taken in palette order.
 *
 * An empty bucket takes the neutral and does not consume a slot, so a null in
 * the middle of a series does not push the buckets after it down the palette.
 *
 * @param {Array<string|null|undefined>} labels
 * @returns {string[]} one colour per label, in order
 */
export function colorsFor(labels) {
  let slot = 0;

  return labels.map(label =>
    // eslint-disable-next-line no-plusplus
    label == null || label === '' ? NEUTRAL_COLOR : colorAt(slot++)
  );
}

/**
 * Colours for a whole series, assigned by position so the palette leads with the
 * brand colours. Use where the buckets are already ordered and stable.
 *
 * @param {number} count
 * @returns {string[]}
 */
export function colorScale(count) {
  return Array.from({ length: count }, (_, index) => colorAt(index));
}
